/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import AppTable from 'components/appTable';
import Gap from 'components/gap';
import PanelBoxLayout from 'components/layout/panelBox';
import { useActions } from 'common/hooks/useActions';
import PrimaryButton from 'components/form/buttons/primaryButton';
import TicketFilterPopup from './lib/filterForm';
import convertToLocalTime from 'common/helpers/convertToLocalTime';

import * as S from './styles';
import * as T from 'common/types';
import { Spin } from 'antd';

// const urgencyMap = {
//   '1': 'High',
//   '2': 'Medium',
//   '3': 'Low'
// };

const priorityMap = {
  '1': 'Critical',
  '2': 'High',
  '3': 'Moderate',
  '4': 'Low',
  '5': 'Planning'
};

const statusMap = {
  '1': 'New',
  '2': 'In Progress',
  '3': 'On Hold',
  '4': 'Awaiting User Info',
  '5': 'Awaiting Evidence',
  '6': 'Resolved',
  '7': 'Closed',
  '8': 'Canceled'
};

const PAGE_SIZE = 50;

interface IProps {
  data: any[];
  loading: boolean;
  hasMore?: boolean;
  infiniteScroll?: boolean;
  error: null | object;
}

export default function IncidentsTable({ data, loading, infiniteScroll, error }: IProps) {
  const { ticketsIncidentRequestActionNew } = useActions();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    status: ''
  });
  const [reqData, setReqData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const loadMore = useCallback(() => {
    if (loading) return;
    // if (observer.current) observer.current.disconnect();
    setPage((prev) => prev + 1);
    // if (node) observer.current.observe(node);
  }, [loading, infiniteScroll]);

  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage: !error && hasMore ? true : false,
    onLoadMore: () => loadMore(),
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    // disabled: Boolean(error),
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 400px 0px'
  });

  const fetchData = () => {
    ticketsIncidentRequestActionNew(meta, page, PAGE_SIZE);
  };

  function getLabelFromMap<T extends Record<string, string>>(map: T, key: string | number): string {
    const keyStr = key?.toString();
    return map[keyStr] || '-';
  }

  useEffect(() => {
    if (data?.length < PAGE_SIZE) {
      setHasMore(false);
    } else {
      setHasMore(true);
    }
  }, [data]);

  useEffect(() => {
    if (infiniteScroll) fetchData();
  }, [page, meta]);

  useEffect(() => {
    if (Array.isArray(data)) {
      if (page === 1 && meta.status === '') {
        setReqData(data);
        return;
      }

      if (meta.status === '') {
        setReqData((prev) => [...prev, ...data]);
        return;
      }

      if (hasMore) setFilteredData((prev) => [...prev, ...data]);
    }
  }, [data]);

  return (
    <S.Content>
      <PanelBoxLayout>
        <S.Head>
          {/* <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e })}
            placeholder="Search"
            inputStyle={{ height: 36, borderRadius: 10, fontSize: 12 }}
            style={{ width: 500 }}
            value={meta.search}
          /> */}
          <TicketFilterPopup setFilter={setMeta} filter={meta} setPage={setPage} />
        </S.Head>
        <Gap size="20px" />
        <AppTable
          headers={[
            { id: 1, title: 'Name', dir: 'left' },
            { id: 2, title: 'Subject', dir: 'left' },
            { id: 4, title: 'Open Date', dir: 'left' },
            { id: 7, title: 'Priority', dir: 'left' },
            { id: 8, title: 'State', dir: 'left' },
            { id: 9, title: '' }
          ]}
          // lastItemRef={lastItem}
          loading={loading}
          data={meta.status === '' ? reqData : filteredData}
          altanfeethiFields={{
            name: (item: T.Pages.TicketList) =>
              item?.user?.name ? (
                <S.Div className="text-left">
                  {item?.user?.name?.length > 30 ? item?.user?.name?.substring(0, 30) + '...' : item?.user?.name}
                </S.Div>
              ) : (
                <S.Div className="text-left">-</S.Div>
              ),
            subject: (item: T.Pages.TicketList) =>
              item?.short_description ? (
                <S.Div className="text-left">
                  {item?.short_description?.length > 20
                    ? item?.short_description?.replace(/\r\n/g, '\n')?.substring(0, 20) + '...'
                    : item?.short_description?.replace(/\r\n/g, '\n')}
                </S.Div>
              ) : (
                <S.Div className="text-left">-</S.Div>
              ),
            createdAt: (item: T.Pages.TicketList) => (
              // <S.Div className="text-left">{moment(item?.sys_created_on).format('YYYY.MM.DD - HH:MM')}</S.Div>
              <S.Div className="text-left">{convertToLocalTime(item?.sys_created_on).toLocaleString()}</S.Div>
            ),
            priority: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">{getLabelFromMap(priorityMap, item?.priority)}</S.Div>
            ),
            status: (item: T.Pages.TicketList) => (
              <S.Div className="text-left">{getLabelFromMap(statusMap, item?.state)}</S.Div>
            ),
            edit: (item: T.Pages.TicketList) => (
              <PrimaryButton
                style={{
                  height: 28,
                  fontSize: 12,
                  background: '#8A93AC',
                  color: 'var(--white)',
                  width: 94,
                  margin: 'auto',
                  borderRadius: 8
                }}
                link={`/panel/ticket/tickets/incidents/${item?.sys_id}`}
              >
                Detail
              </PrimaryButton>
            )
          }}
        />
        {!error && (
          <div ref={infiniteRef} style={{ display: 'flex', justifyContent: 'center', padding: 20, width: '100%' }}>
            {loading && <Spin />}
          </div>
        )}
      </PanelBoxLayout>
      {/* {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )} */}
    </S.Content>
  );
}
